import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { AbstractPattern } from '../components/Branding';
import { FiTarget, FiBox, FiTrendingUp, FiCheckCircle, FiShield, FiMic, FiCamera, FiCoffee } from 'react-icons/fi';

export default function Overview() {
  const cards = [
    { title: "The Problem", desc: "Fragmented spreadsheets, lost emails, and zero real-time visibility during setup.", icon: FiTarget, color: "text-red-400", bg: "bg-red-500/10" },
    { title: "The Solution", desc: "A unified Studio OS that centralizes guest matrix, vendor nodes, and live telemetry.", icon: FiBox, color: "text-[#9D4EDD]", bg: "bg-[#9D4EDD]/10" },
    { title: "The Outcome", desc: "Flawless execution with an 85% reduction in manual coordination overhead.", icon: FiTrendingUp, color: "text-[#E5C158]", bg: "bg-[#E5C158]/10" }
  ];

  const modules = [
    { name: "Catering", icon: FiCoffee },
    { name: "A/V Tech", icon: FiMic },
    { name: "Media", icon: FiCamera },
    { name: "Security", icon: FiShield }
  ];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full space-y-10 overflow-y-auto pb-24 pr-2 no-scrollbar">
      <div className="relative p-10 rounded-[2.5rem] bg-gradient-to-br from-[#12121c] to-[#050508] border border-white/5 overflow-hidden">
        <AbstractPattern type="nebula" />
        <div className="relative z-10 max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E5C158]/10 border border-[#E5C158]/20 text-[#E5C158] text-[10px] font-black uppercase tracking-widest mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-[#E5C158] animate-pulse"></span>
            System Status: Optimal
          </div>
          <h1 className="text-4xl md:text-6xl font-black text-white tracking-tighter mb-6 leading-[0.9]">
            THE FUTURE OF <br/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#9D4EDD] to-[#E5C158]">EVENT PRECISION</span>
          </h1>
          <p className="text-lg text-[#94A3B8] leading-relaxed max-w-2xl">
            EventNova is the world's first futuristic Studio OS designed specifically for boutique planners and luxury hosts. We replace chaos with command.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {cards.map((card, idx) => (
          <div key={idx} className="glass-panel p-8 border-white/5 hover:border-[#9D4EDD]/30 transition-all group relative overflow-hidden h-full flex flex-col">
            <div className={`mb-6 p-4 rounded-2xl w-fit ${card.bg} ${card.color}`}>
              <SafeIcon icon={card.icon} className="text-3xl" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">{card.title}</h3>
            <p className="text-sm text-[#94A3B8] leading-relaxed flex-1">{card.desc}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="glass-panel p-8 relative overflow-hidden border-white/5">
          <div className="absolute top-0 right-0 p-8 opacity-5">
            <SafeIcon icon={FiCheckCircle} className="text-[12rem] text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-8">Target Verticals</h2>
          <div className="grid grid-cols-2 gap-6">
            {[
              "Boutique Weddings",
              "Tech Summits",
              "Luxury Galas",
              "Art Exhibitions"
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-4 group cursor-default">
                <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center text-[#E5C158] group-hover:bg-[#E5C158]/10 transition-colors">
                   <SafeIcon icon={modules[i % 4].icon} />
                </div>
                <span className="text-white/80 font-bold text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="glass-panel p-8 flex flex-col justify-center items-center text-center bg-gradient-to-tr from-[#9D4EDD]/10 to-transparent border-[#9D4EDD]/20">
           <h3 className="text-[#E5C158] font-black uppercase tracking-[0.3em] text-xs mb-4">Production Credit</h3>
           <div className="flex items-center gap-4 mb-4">
             <div className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white font-black text-xs">LL</div>
             <div className="w-1 h-1 rounded-full bg-white/20"></div>
             <div className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white font-black text-xs">DL</div>
           </div>
           <p className="text-white font-bold italic">
             Made by <span className="text-[#9D4EDD]">LingLink AI</span> and <span className="text-[#E5C158]">DigLab AI</span>
           </p>
           <p className="text-[10px] text-[#94A3B8] mt-2 uppercase tracking-widest">Global Studio Distribution v2.4.0</p>
        </div>
      </div>
    </motion.div>
  );
}